/**
 * Trial model — the frontend mirror of vernot's
 * `supabase/functions/_shared/trial-entitlements.ts`.
 *
 * ── The model (founder rulings 2026-08-02, 2026-08-08) ──────────────────────
 * The old shape was a PERPETUAL free tier (dispatcher 25 credits/week, broker
 * 10 credits/month) beside a short no-card trial of a paid tier. That gave the
 * product away forever to anyone who never converted. The shape now:
 *
 *   - Every non-carrier account is ON the 14-day trial from signup, no card and
 *     no plan choice required: the `companies_auto_trial_window` trigger (vernot
 *     migration 20260808120658) stamps the window as soon as the company has a
 *     non-carrier role. Picking a plan names the tier; it never restarts the
 *     clock. Before that trigger a fresh account had no window, resolved to a
 *     0-credit bucket, and read "0 / 0" from its first second.
 *   - During the trial, EVERY plan is capped at 100 credits — a trial of
 *     Broker Scale is not 7,500 free credits.
 *   - During the trial, sealed-record artifacts (audit-trail PDFs, record
 *     packs, the vault CSV) cannot be downloaded. Records still SEAL and still
 *     accrue; you own and can export them the moment you pay.
 *   - When the 14 days are up, dispatchers and brokers get no free allowance.
 *     They pick a plan or they stop.
 *
 * The CARRIER free tier ($0 forever: unlimited broker lookups + a registry
 * profile) is untouched — it is the distribution engine, not a trial.
 *
 * ⚠ NOTHING HERE ENFORCES ANYTHING. Every value in this file is a mirror kept
 * for rendering: the enforcing copies are `public.check_usage_limit` (credits)
 * and the `checkSealedRecordDownload` gate wired into report-generate /
 * vault-export (downloads). If these drift, the UI lies about a limit the
 * server still applies — it never grants one. Change all three together.
 */

/** Length of the one no-card trial window, in days. */
export const TRIAL_DAYS = 14;

/**
 * Hard ceiling on credits while on trial, applied to EVERY plan.
 * Mirrors TRIAL_CREDIT_CAP in vernot `_shared/trial-entitlements.ts` and the
 * `v_trial_cap` constant in `public.check_usage_limit`.
 */
export const TRIAL_CREDIT_CAP = 100;

/**
 * The one string shown when a paid-only action is blocked. Mirrors
 * TRIAL_DOWNLOAD_BLOCKED_MESSAGE server-side so the tooltip and the API error
 * read identically — a user who bypasses the disabled button and hits the
 * endpoint should not get a different story.
 */
export const PAID_ONLY_MESSAGE = "This function is only available on paid plans.";

/** Machine-readable code the gated endpoints return (HTTP 402). */
export const TRIAL_DOWNLOAD_BLOCKED_CODE = "trial_download_locked";

export type TrialPosture =
  /** Paying (Paddle/Stripe subscription, or a webhook-confirmed active plan). */
  | "paid"
  /** Comped / internal / test account: entitled, no clock, never paywalled. */
  | "comped"
  /** Inside the 14-day no-card window. */
  | "trial"
  /** The window closed and nothing was bought. Paywalled. */
  | "trial_expired"
  /** Carrier on the permanent $0 tier. */
  | "free_forever"
  /** Signed up, picked no plan, never started a clock. */
  | "no_plan"
  /** Not resolved yet, or the company row is unreadable. Treated as entitled
   *  so a slow query never flashes a paywall at a paying customer. */
  | "unknown";

export interface TrialCompanyRow {
  logistics_role: string | null;
  subscription_tier: string | null;
  billing_status: string | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  billing_exempt: boolean | null;
  stripe_subscription_id: string | null;
  paddle_subscription_id: string | null;
  is_test_tenant: boolean | null;
}

const FREE_FOREVER_ROLES = new Set(["carrier", "trucker"]);

/** True for roles whose $0 state is permanent, not a trial. */
export function isFreeForeverRole(role: string | null | undefined): boolean {
  return FREE_FOREVER_ROLES.has((role ?? "").trim().toLowerCase());
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * Classify a company into exactly one posture. Local fallback for when the
 * server RPC `company_trial_posture` is unavailable (e.g. the migration has not
 * been deployed yet). Keep the ORDER identical to resolveTrialPosture() in
 * vernot and to the SQL in check_usage_limit.
 */
export function resolveTrialPosture(
  row: TrialCompanyRow | null | undefined,
  nowMs: number = Date.now(),
): TrialPosture {
  if (!row) return "unknown";

  // A real subscription outranks every trial signal.
  if (row.paddle_subscription_id || row.stripe_subscription_id) return "paid";

  const status = (row.billing_status ?? "").trim().toLowerCase();
  if (status === "active" || status === "trialing") return "paid";

  // Test tenants are never paywalled — QA fixtures churn through expiry dates.
  if (row.is_test_tenant === true) return "comped";

  const endsMs = ms(row.trial_ends_at);

  // Comp-shaped: exempt from billing with NO clock. billing_exempt alone must
  // NOT read as comped — set-plan sets it on every no-card trial, so only the
  // absence of a clock distinguishes a genuine comp.
  if (row.billing_exempt === true && endsMs === null) return "comped";

  if (status === "expired") return "trial_expired";

  if (endsMs !== null) {
    if (endsMs > nowMs) return "trial";
    return isFreeForeverRole(row.logistics_role) ? "free_forever" : "trial_expired";
  }

  if (isFreeForeverRole(row.logistics_role)) return "free_forever";
  return "no_plan";
}

/**
 * Whole days left, rounded UP so a window with minutes left still reads
 * "1 day left" rather than "0". Null when there is no window; never negative.
 */
export function trialDaysRemaining(
  trialEndsAt: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  const endsMs = ms(trialEndsAt);
  if (endsMs === null) return null;
  return Math.max(0, Math.ceil((endsMs - nowMs) / 86_400_000));
}

export interface TrialCountdown {
  /** Whole days left in the window, rounded up. Always >= 1. */
  daysLeft: number;
  /** Ready-to-render line: "14 days left" / "1 day left". */
  label: string;
}

/**
 * Display-only: the countdown for a company that is inside its no-card trial
 * window, or null when the company is not on a trial at all.
 *
 * Classification runs through `resolveTrialPosture` rather than a local
 * billing_status check so the billing hero, the usage meter and the server
 * gates agree on what "on trial" means. In particular this returns null for a
 * real subscription (Paddle/Stripe id, or billing_status active/trialing — a
 * paid subscription's own trial is billed by the processor and its window is
 * cleared by paddle-webhook), for a comped/test tenant, for an expired window,
 * and for a fresh signup that has never started a clock. Those last two show
 * their own states; neither is a running trial with days to count.
 */
export function trialCountdown(
  row: TrialCompanyRow | null | undefined,
  nowMs: number = Date.now(),
): TrialCountdown | null {
  if (!row) return null;
  if (resolveTrialPosture(row, nowMs) !== "trial") return null;
  const daysLeft = trialDaysRemaining(row.trial_ends_at, nowMs);
  // trialDaysRemaining floors at 0, and posture "trial" already means the
  // window is open, so 0 here can only be a clock/rounding edge — say nothing
  // rather than render "0 days left" next to a live trial.
  if (daysLeft === null || daysLeft <= 0) return null;
  return { daysLeft, label: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` };
}

/**
 * The `usage_limits` buckets that hold the PRE-PLAN number. A company that has
 * not chosen a plan resolves to one of these (free+dispatcher ->
 * dispatcher-trial, free+broker -> broker-free) and their stored allowance is
 * 0 — the POST-trial paywall, not what a trial is worth. Mirrors
 * PRE_PLAN_USAGE_TIERS in vernot `_shared/trial-entitlements.ts`.
 */
const PRE_PLAN_USAGE_TIERS = new Set(["dispatcher-trial", "broker-free"]);

/** True for the pre-plan buckets above. */
export function isPrePlanUsageTier(effectiveTier: string | null | undefined): boolean {
  return PRE_PLAN_USAGE_TIERS.has((effectiveTier ?? "").trim().toLowerCase());
}

/**
 * Resolve the credit allowance to render for a company on a trial.
 * `planLimit` uses the codebase-wide -1 = unlimited sentinel; `effectiveTier`
 * is the usage_limits bucket the backend resolved.
 *
 *  - A PRE-PLAN bucket takes the cap outright: every non-carrier signup now
 *    starts on the trial before it has picked a plan, and that bucket's
 *    allowance is the paywall's 0 — min(0, 100) = 0 is what made a brand-new
 *    account read "0 / 0 credits".
 *  - Any real plan keeps the CEILING, so for a chosen plan this still only ever
 *    reduces (Verified+ at 60 stays 60).
 *
 * Mirrors `applyTrialCreditCap` in vernot `_shared/trial-entitlements.ts` and
 * the trial branch of `check_usage_limit`. Called without `effectiveTier` it
 * behaves exactly as before.
 */
export function applyTrialCreditCap(
  planLimit: number,
  posture: TrialPosture,
  effectiveTier?: string | null,
): number {
  if (posture !== "trial") return planLimit;
  if (isPrePlanUsageTier(effectiveTier)) return TRIAL_CREDIT_CAP;
  if (planLimit === -1) return TRIAL_CREDIT_CAP;
  return Math.min(planLimit, TRIAL_CREDIT_CAP);
}

/**
 * May this company DOWNLOAD a sealed-record artifact (audit-trail PDF, record
 * pack, vault CSV)? Paid and comped only.
 *
 * "unknown" is allowed on purpose: an unresolved posture must never disable a
 * paying customer's own evidence. The server gate is the real control, so the
 * cost of being wrong here is a button that works and an endpoint that 402s —
 * strictly better than a button that lies to a subscriber.
 */
export function canDownloadSealedRecord(posture: TrialPosture): boolean {
  return posture === "paid" || posture === "comped" || posture === "unknown";
}

/** Is the company locked out pending a plan choice? Drives the full-page wall. */
export function isPaywalled(posture: TrialPosture): boolean {
  return posture === "trial_expired";
}

/** True while the 14-day window is open. */
export function isOnTrial(posture: TrialPosture): boolean {
  return posture === "trial";
}
